import React, {
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { type FileRejection, useDropzone } from "react-dropzone";
import { useQueryClient } from "@tanstack/react-query";
import { Box } from "@mui/material";
import { WarningAmberRounded } from "@mui/icons-material";

import { bulkIngestDocuments } from "api";
import { useNotify } from "hooks";
import { QUERY_KEYS } from "consts";
import { COLORS } from "components/consts";
import { Body2Regular } from "components/typography";
import { CloseButton } from "components/buttons";
import { updateFileCountContext } from "providers/uploadFileCount-provider";
import { extractErrorMessage } from "utils";
import {
  ACCEPTED_TYPES,
  DEFAULT_SITE_ID,
  EXCEL_FILE_TYPES,
  MAX_TOTAL_UPLOAD_SIZE_BYTES,
  SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE,
} from "./consts";
import { DropZoneProps } from "./types";
import { activeZoneStyle } from "./styles";

export const DropZone = ({
  children,
  onUploadCompleted,
  onExcelDrop,
  siteId,
  rejectMessageMode = "notify",
}: DropZoneProps) => {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { updateUploadedCount, updateTotalCount } = useContext(
    updateFileCountContext,
  );
  const [isUploading, setIsUploading] = useState(false);
  const [showErrorDropzone, setShowErrorDropzone] = useState(false);
  const resetCounterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const totalSize = acceptedFiles.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_UPLOAD_SIZE_BYTES) {
        notify.error(SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE);
        return;
      }

      const hasExcel = acceptedFiles.some(
        (f) =>
          EXCEL_FILE_TYPES.includes(f.type) ||
          f.name.toLowerCase().endsWith(".xlsx") ||
          f.name.toLowerCase().endsWith(".xls"),
      );

      if (hasExcel) {
        // All files wait for the dialog; upload happens together after confirmation
        onExcelDrop?.(acceptedFiles);
        return;
      }

      clearTimeout(resetCounterTimeoutRef.current ?? undefined);
      updateUploadedCount(0);
      updateTotalCount(acceptedFiles.length);
      setIsUploading(true);
      try {
        const response = await bulkIngestDocuments(
          siteId ?? DEFAULT_SITE_ID,
          acceptedFiles,
        );
        notify.success(response.message ?? "Successfully uploaded all files.");
        updateUploadedCount(acceptedFiles.length);
        clearTimeout(resetCounterTimeoutRef.current ?? undefined);
        resetCounterTimeoutRef.current = setTimeout(() => {
          updateUploadedCount(0);
          updateTotalCount(0);
        }, 3000);
        onUploadCompleted?.();
        queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
      } catch (error: unknown) {
        const status = (error as any)?.response?.status;
        notify.error(
          status === 413
            ? SERVER_LIMIT_EXCEEDED_ERROR_MESSAGE
            : extractErrorMessage(
                error,
                "Failed to upload files. Please try again.",
              ),
        );
        updateTotalCount(0);
      } finally {
        setIsUploading(false);
      }
    },
    [
      notify,
      onExcelDrop,
      onUploadCompleted,
      queryClient,
      siteId,
      updateUploadedCount,
      updateTotalCount,
    ],
  );

  const onDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      if (rejections.length === 0) return;
      const hasInvalidType = rejections[0].errors.some(
        (e) => e.code === "file-invalid-type",
      );
      if (rejectMessageMode === "notify") {
        notify.error(
          hasInvalidType
            ? "Only PDF and XLSX files are accepted."
            : "One or more files could not be uploaded. Please try again.",
        );
      } else {
        setShowErrorDropzone(true);
      }
    },
    [notify, rejectMessageMode],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_TYPES,
    disabled: isUploading,
    onDrop,
    onDropRejected,
  });

  return (
    <Box {...getRootProps()} sx={activeZoneStyle(isDragActive)}>
      <input {...getInputProps()} />
      {children}
      {showErrorDropzone && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            borderRadius: "8px",
            backgroundColor: "rgba(231, 2, 2, 0.08)",
            border: `1px solid ${COLORS.red}`,
          }}
        >
          <WarningAmberRounded sx={{ color: COLORS.red }} />
          <Body2Regular sx={{ color: COLORS.red, flex: 1 }}>
            File type not allowed
          </Body2Regular>
          <Box onClick={(e) => e.stopPropagation()}>
            <CloseButton
              color={COLORS.red}
              onClick={() => setShowErrorDropzone(false)}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
